import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import { FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { Fields } from 'src/app/utils/fields';
import { VipLevelSettings } from 'src/app/shared/models/constants/vip-level-settings';
import { VipFormBuilderService } from 'src/app/core/services/vip-form-builder/vip-form-builder.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';

@Component({
  selector: 'app-add-vip-modal',
  templateUrl: './add-vip-modal.component.html',
  styleUrls: ['./add-vip-modal.component.scss'],
})
/**
 * Modal for add a business level.
 */
export class AddVipModalComponent {
  formVip: FormGroup = new FormGroup({});
  vipLevels: VipLevelSettings[] = [];
  vipFields = [
    Fields.LABEL,
    Fields.BUSINESS_LEVEL_ID,
    Fields.NAME,
    Fields.LEVEL_POINTS,
    Fields.DISCOUNT,
  ];
  labels = {
    createTitle: 'add-vip-modal.title',
    saveChanges: 'save-changes',
    cancel: 'cancel',
    titleDesc: 'add-vip-modal.title-desc',
    vipLevelName: 'vip-level-name',
    inkRequired: 'ink-required-points-to-level',
    discount: 'discount',
    editSeriesTitle: 'series-form-modal.title-edit-series',
    editVipTitle: 'series-form-modal.title-edit-vip',
    nameLabel: 'series-form-modal.name-label',
    tableEditTitle: 'series-form-modal.table-edit-title',
    currentSeason: 'current-season',
    nextSeason: 'next-season-capitalized',
    startsOn: 'starts-on',
    endsOn: 'ends-on',
    changesApplyAllRegions: 'changes-apply-all-regions',
    savingError: 'unexpected-error',
    success: 'success-updating-data',
    addVipLevel: 'series-form-modal.add-vip-level',
  };
  errorMsg = {
    requiredMsg: 'series-form-modal.error-messages.required',
    specialCharacterMsg: 'series-form-modal.error-messages.special-character',
    minLengthMsg: 'series-form-modal.error-messages.min-length',
    minInkLevelValueMsg: 'series-form-modal.error-messages.min-ink-level-value',
    maxInkLevelValueMsg: 'series-form-modal.error-messages.max-ink-level-value',
    minDiscountValueMsg: 'series-form-modal.error-messages.min-discount-value',
    maxDiscountValueMsg: 'series-form-modal.error-messages.max-discount-value',
  };
  toggleMessages = {
    success: 'add-vip-modal.success-message',
    error: 'add-vip-modal.error-message',
  };
  loading: boolean = false;

  /**
   * Constructor
   */
  constructor(
    private readonly dialogRef: MatDialogRef<AddVipModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      familyId: number;
      vipSeriesCount: number;
    },
    private readonly vipFormBuilder: VipFormBuilderService,
    private readonly translationService: TranslationService,
    private readonly snackBarService: SnackBarService,
    private readonly userService: UserService
  ) {
    this.vipLevels.push({
      label: 'Vip ' + (this.data.vipSeriesCount + 1),
      id: 'vip',
      markedForDeletion: false,
    });
    this.formVip = this.vipFormBuilder.createVipForm(
      this.vipLevels,
      this.vipFields
    );
    this.translationService.translateObject(this.labels);
    this.translationService.translateObject(this.errorMsg);
    this.translationService.translateObject(this.toggleMessages);
  }

  /**
   * Action to close the dialog
   */
  onDialogClose() {
    this.dialogRef.close(MatDialogResponse.FAIL);
  }

  /**
   * Add business level to the family id
   */
  addBusinessLevel() {
    this.loading = true;
    const firstKey = Object.keys(this.formVip.controls)[0];
    const firstFormValue = this.formVip.get(firstKey)?.value;

    const data = {
      familyId: this.data.familyId,
      vipConfig: firstFormValue,
    };
    this.userService.createSingleBusinessVipLevel(data).subscribe({
      next: () => {
        this.snackBarService.success(this.toggleMessages.success);
        this.dialogRef.close(MatDialogResponse.SUCCESS);
        this.loading = false;
      },
      error: () => {
        this.snackBarService.error(this.toggleMessages.error);
        this.loading = false;
      },
    });
  }

  /**
   * Cancel button click action
   */
  clickCancel(): void {
    this.dialogRef.close();
  }

  /**
   * retrieves the form control associated with the specified family and language.
   * @param {string} vipLevel
   * @param {string} field
   * @return {FormControl}
   */
  getControlByVipType(vipLevel: string, field: string): FormControl {
    return this.formVip.get([vipLevel, field].join('.')) as FormControl;
  }

  /**
   * Define generic errors
   * @return {string[]}
   */
  getGenericErrors(): string[] {
    const messages: string[] = [];
    Object.keys(this.formVip.controls).forEach((vipId) => {
      const group = this.formVip.get(vipId) as FormGroup;
      this.collectGroupErrors(group, messages);
    });
    return [...new Set(messages)];
  }

  /**
   * @param {FormGroup} group
   * @param {string[]} messages
   */
  private collectGroupErrors(group: FormGroup, messages: string[]): void {
    Object.keys(group.controls).forEach((field) => {
      const control = group.get(field);
      if (control?.errors && (control.touched || control.dirty)) {
        messages.push(...this.getFieldErrorMessages(field, control.errors));
      }
    });
  }

  /**
   * @param {string} field
   * @param {ValidationErrors} errors
   * @return {string[]}
   */
  private getFieldErrorMessages(
    field: string,
    errors: ValidationErrors
  ): string[] {
    const msgs: string[] = [];
    if (errors['required']) msgs.push(this.errorMsg.requiredMsg);
    if (errors['minlength']) msgs.push(this.errorMsg.minLengthMsg);
    if (errors['pattern']) msgs.push(this.errorMsg.specialCharacterMsg);
    if (errors['min']) {
      msgs.push(
        field === Fields.LEVEL_POINTS
          ? this.errorMsg.minInkLevelValueMsg
          : this.errorMsg.minDiscountValueMsg
      );
    }
    if (errors['max']) {
      msgs.push(
        field === Fields.LEVEL_POINTS
          ? this.errorMsg.maxInkLevelValueMsg
          : this.errorMsg.maxDiscountValueMsg
      );
    }
    return msgs;
  }
}
